import { useEffect, useState } from 'react';
import { Search, X } from '@/components/icons';
import { Input } from './Input';

interface Props {
  /** Called with the trimmed text once typing pauses (and on clear). */
  onSearch: (value: string) => void;
  placeholder: string;
  /** Accessible name for the input. */
  label: string;
  /** Pulse the icon while results load. */
  busy?: boolean;
  /** Extra classes for the input (e.g. a minimum width). */
  inputClassName?: string;
}

/**
 * Filter-bar search box that searches once typing pauses (300 ms) instead of on every
 * keystroke — list searches hit the server, so a request per letter made pages lag and
 * flicker. Has a clear (×) button; Esc clears too.
 */
export function SearchBox({ onSearch, placeholder, label, busy, inputClassName = '' }: Props) {
  const [text, setText] = useState('');
  useEffect(() => {
    const t = setTimeout(() => onSearch(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text, onSearch]);
  return (
    <div className="relative w-full">
      <Search
        size={12}
        className={`pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 sm:left-2.5 ${busy ? 'animate-pulse text-accent' : 'text-[var(--text-muted)]'}`}
        strokeWidth={2.25}
      />
      <Input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') setText(''); }}
        placeholder={placeholder}
        className={`!pl-5 !pr-7 sm:!pl-8 [&::-webkit-search-cancel-button]:hidden ${inputClassName}`}
        aria-label={label}
      />
      {text && (
        <button
          type="button"
          onClick={() => setText('')}
          className="absolute right-1 top-1/2 flex h-5 min-h-0 w-5 -translate-y-1/2 items-center justify-center rounded text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)] sm:right-1.5"
          aria-label="Clear search"
          title="Clear"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}
